import React, {useEffect, useState} from 'react';
import {
  AddNewButton,
  ComponentWrapper,
  SearchWrapper
} from './ExternalAuditStyle';
import {Button, Modal, Typography, useTheme} from '@mui/material';
import CollapsibleTable from '../../sub-modules-menu/collapsible-table/CollapsibleTable';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE
} from 'src/routes/BaseRoute';
import FormConstants from '@shared/constants/FormConstants';
import ModuleSearchFilter from '../module-search-filter/ModuleSearchFilter';
import {
  addNewExternalAuditFile,
  getSearchForm
} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import Stack from '@mui/system/Stack/Stack';
import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs/custom-breadcrumbs';
import EncryptUtils from 'src/utils/EncryptUtils';
import ExcelJS from 'exceljs';

interface ExternalAuditProps {
  module: string;
}

interface StateProps {
  uiConfig: object;
  addNew: boolean;
  addNewConfig: any;
  loader: boolean;
  isModalOpen: boolean;
}

const ExternalAudit = (props: ExternalAuditProps): React.ReactElement => {
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const [state, setState] = useState<StateProps>({
    uiConfig: [],
    addNew: false,
    addNewConfig: [],
    loader: false,
    isModalOpen: false
  });
  const {addNew, addNewConfig, uiConfig, isModalOpen} = state;

  const openModal = () => {
    setState((prev: any) => ({
      ...prev,
      isModalOpen: true
    }));
  };

  const closeModal = () => {
    setState((prev: any) => ({
      ...prev,
      isModalOpen: false
    }));
    // eslint-disable-next-line no-use-before-define
    setSelectedFile(null);
  };
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const uploadFile = async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Read the Excel file
      const workbook = new ExcelJS.Workbook();
      const fileReader = new FileReader();

      return new Promise((resolve, reject) => {
        fileReader.readAsArrayBuffer(file);

        fileReader.onload = async event => {
          try {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0]; // Get the first worksheet
            const extractedData: any[] = [];

            // Extract header from the first row
            const headerRow = worksheet.getRow(1).values as string[];

            // Loop through the remaining rows
            worksheet.eachRow((row: any, rowNumber) => {
              if (rowNumber > 1) {
                const rowData: any = {};
                row.values.forEach((cellValue: any, index: any) => {
                  if (index > 0) {
                    const columnName = headerRow[index];
                    rowData[columnName] = cellValue;
                  }
                });
                extractedData.push(rowData);
              }
            });

            // Prepare the data to be encrypted
            const requestData = {
              extracted_data: extractedData,
              sql_data: addNewConfig?.search_results?.sql_data || {} // Include sql_data safely
            };

            // Encrypt and send the extracted data
            const encryptRequestData = EncryptUtils.encryptedData(requestData);
            formData.append('tk', encryptRequestData);

            // Send the processed data
            await addNewExternalAuditFile(formData);

            enqueueSnackbar(
              'File submitted successfully',
              SnackBarConfig.getSuccess()
            );

            resolve(); // Resolve promise after success
          } catch (error) {
            console.error('Error processing file:', error);
            enqueueSnackbar('Error Uploading File', SnackBarConfig.getError());
            reject(error);
          }
        };

        fileReader.onerror = () => {
          enqueueSnackbar('Error reading file', SnackBarConfig.getError());
          reject(new Error('File reading error'));
        };
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar('Error Uploading File', SnackBarConfig.getError());
      throw error; // Ensure error is thrown and caught in handleFileSelect
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const fileName = selectedFile.name;
      const fileType = fileName.split('.').pop()?.toLowerCase();

      if (fileType === 'csv' || fileType === 'xls' || fileType === 'xlsx') {
        setSelectedFile(selectedFile);
        closeModal();

        try {
          await uploadFile(selectedFile); // Wait for file upload to complete
          // eslint-disable-next-line no-use-before-define
          handleCancelButton(); // Trigger cancel button only after success
        } catch (error) {
          console.error('File upload failed:', error);
          enqueueSnackbar('Error uploading file', SnackBarConfig.getError());
        }
      } else {
        enqueueSnackbar(
          'Please select a valid CSV or Excel file',
          SnackBarConfig.getError()
        );
      }
    }
  };

  const init = async () => {
    try {
      const res = await getSearchForm(applicationName, props.module);

      setState((prev: any) => ({
        ...prev,
        uiConfig: res?.sections?.[0],
        addNewConfig: res?.sections?.[0]?.add_new?.searchForm
      }));
    } catch (error) {
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewButton = () => {
    setState((prev: any) => ({
      ...prev,
      addNew: true
    }));
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${FormConstants.MENU_ITEM.EXTERNAL_AUDIT}`,
      {
        state: {}
      }
    );
  };

  const handleCancelButton = () => {
    // openModal();
    setState((prev: any) => ({
      ...prev,
      addNew: false
    }));
    // navigate(`/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}`, {
    //   state: {}
    // });
  };

  const handleImportButton = () => {
    if (!isModalOpen) {
      openModal();
    }
  };

  const handleButtonSubmit = () => {
    //  handleCancelButton();
  };

  const renderModuleSearchFilter = () => {
    return (
      <Stack spacing={3} px={3}>
        <CustomBreadcrumbs
          heading={'Audit'}
          links={[]}
          sx={{
            mt: 2
          }}
        />
        <ModuleSearchFilter
          uiFieldsData={addNewConfig}
          handleButtonSubmit={() => handleButtonSubmit()}
          handleCancelButton={() => handleCancelButton()}
          handleImportButton={() => handleImportButton()}
        />
      </Stack>
    );
  };

  const renderAddNewButton = () => {
    return (
      <AddNewButton>
        <LoadingButton
          type="submit"
          variant="contained"
          color="success"
          sx={{
            bgcolor: theme.palette.primary.main
          }}
          onClick={() => handleAddNewButton()}>
          {t('PRIVATE.EXTERNAL_AUDIT.ADD_NEW', {
            ns: 'private'
          })}
        </LoadingButton>
      </AddNewButton>
    );
  };

  const renderCollapsibleTable = () => {
    return (
      <CollapsibleTable
        sectionUIData={uiConfig}
        sectionRowsData={[]}
        module={props?.module}
      />
    );
  };

  const renderModuleSearchPage = () => {
    return (
      <SearchWrapper>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          px={3}
          justifyContent="space-between">
          <CustomBreadcrumbs
            heading={'External Audit'}
            links={[]}
            sx={{
              mt: 2
            }}
          />
        </Stack>
        {renderAddNewButton()}

        {renderCollapsibleTable()}
      </SearchWrapper>
    );
  };

  return (
    <ComponentWrapper>
      {addNew ? renderModuleSearchFilter() : renderModuleSearchPage()}

      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={closeModal}
          sx={{
            '& .MuiStack-root': {
              borderRadius: '8px'
            }
          }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 380,
              bgcolor: 'background.paper',
              p: 4
            }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{alignSelf: 'flex-start'}}>
              Upload CSV or Excel file
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              sx={{alignSelf: 'flex-start'}}>
              <label htmlFor="contained-button-file">
                <Typography
                  variant="h6"
                  gutterBottom
                  color={'white'}
                  fontSize={10}
                  sx={{
                    backgroundColor: theme.palette.primary.dark,
                    py: '5px',
                    px: '10px',
                    borderRadius: '8px'
                  }}>
                  Choose File
                </Typography>
                <input
                  accept=".csv, .xls, .xlsx"
                  id="contained-button-file"
                  multiple={false}
                  type="file"
                  onChange={handleFileSelect}
                  style={{
                    display: 'none',
                    marginTop: '10px'
                  }}
                />
              </label>
              <Typography
                variant="body1"
                gutterBottom
                sx={{marginLeft: '10px'}}>
                {selectedFile ? selectedFile.name : 'No file chosen'}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{mt: 2, width: '100%'}}>
              <Button
                type="button"
                variant="outlined"
                onClick={closeModal}
                sx={{
                  backgroundColor: theme.palette.primary.light
                }}>
                Close
              </Button>
            </Stack>
          </Stack>
        </Modal>
      )}
    </ComponentWrapper>
  );
};

export default ExternalAudit;
